import {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext
} from "react";
import { ConsoleLog } from "@inntechcorp/it-design";
import Cookies from "js-cookie";
import i18next from "i18next";
import { toast } from "sonner";

import { CoreConfig } from "Config/CoreConfig";
import BackOfficeContext from "Context/BackOfficeContext";
import RequestCountsSocketBridge from "Context/RequestCountsSocketBridge";
import { setSocketConnectedForCounts } from "Hooks/useRequestCountsSync";
import { ChildrenProps } from "Types/ChildrenProps";

type SocketEventCallback = (data: any) => void;

export type BackofficeSocketContextProps = {
  status    : number;
  subscribe : (eventName: string, callback: SocketEventCallback) => void;
  unsubscribe: (eventName: string, callback: SocketEventCallback) => void;
};

const BackofficeSocketContext = createContext<BackofficeSocketContextProps>(
  {} as BackofficeSocketContextProps
);

BackofficeSocketContext.displayName = "BackofficeSocketContext";

/**
 * 0 - not connected
 * 1 - connecting
 * 2 - connected
 * 3 - was reconnected
 * 4 - disconnected
 * 5 - socket error
 */

export const BackofficeSocketProvider = ({ children }: { children: ChildrenProps }) => {

  const { isLoggedIn } = useContext(BackOfficeContext);

  const [status, SetStatus]       = useState(0);
  const connectionRef             = useRef<any>(null);
  const isListeningRef            = useRef(false);
  const subscribersRef            = useRef<Map<string, Set<SocketEventCallback>>>(new Map());

  const subscribe = useCallback((eventName: string, callback: SocketEventCallback) => {
    if (!subscribersRef.current.has(eventName)) {
      subscribersRef.current.set(eventName, new Set());
    }
    subscribersRef.current.get(eventName)!.add(callback);
  }, []);

  const unsubscribe = useCallback((eventName: string, callback: SocketEventCallback) => {
    subscribersRef.current.get(eventName)?.delete(callback);
  }, []);

  const emit = useCallback((eventName: string, data: any) => {
    subscribersRef.current.get(eventName)?.forEach(cb => {
      try { cb(data); } catch (e) {
        ConsoleLog.double("🔴 [BO Socket] Subscriber error", e, "error");
      }
    });
  }, []);

  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.close();
      connectionRef.current = null;
    }
    SetStatus(0);
    setSocketConnectedForCounts(false);
  }, []);

  const connect = useCallback(async () => {

    if (isListeningRef.current) return;

    const neffos = (window as any).neffos;
    if (!neffos || typeof neffos.dial !== "function") {
      ConsoleLog.double(
        "🔴 [BO Socket]",
        "Neffos is not loaded. Skipping connection attempt.",
        "error"
      );
      SetStatus(5);
      return;
    }

    const token     = Cookies.get("bo-token") || "";
    const wsPath    = CoreConfig.Socket?.Path || "/ws";
    const namespace = CoreConfig.Socket?.Namespace || "backoffice";

    if (!token) {
      ConsoleLog.single("🔴 [BO Socket] No auth token found. Skipping.");
      return;
    }

    isListeningRef.current = true;
    SetStatus(1);
    ConsoleLog.single("📡 [BO Socket] Connecting...");

    try {
      const conn = await neffos.dial(wsPath, {
        [namespace]: {
          _OnNamespaceConnect() {
            ConsoleLog.single("📡 [BO Socket] Namespace connecting...");
          },
          async _OnNamespaceConnected(nsConn: any) {

            if (nsConn.conn.wasReconnected()) {
              SetStatus(3);
              ConsoleLog.single("📡 [BO Socket] Reconnected");
            } else {
              SetStatus(2);
              ConsoleLog.single("📡 [BO Socket] Connected");
            }

            /**
             * Client'ın private odasına katıl.
             * --
             * Backend `client_new_request_counts` event'ini bu room'a push'luyor;
             * room'a join olmadan event akmıyor. Reconnect senaryosunda da
             * nsConn taze olduğundan tekrar join etmek gerekiyor (bu handler
             * her iki durumda da çalışıyor).
             *
             * Room'a girilemezse `socketConnectedForCounts` false bırakılarak
             * 10sn'lik polling fallback'i devrede tutuluyor.
             */
            const clientSlug = CoreConfig.ClientSlug;

            if (!clientSlug) {
              ConsoleLog.single(
                "⚠️ [BO Socket] No clientSlug available; skipping private room join. Polling fallback stays active."
              );
              return;
            }

            const roomName = `${clientSlug}_private`;

            try {
              await nsConn.askRoomJoin(roomName);
              ConsoleLog.single(`📡 [BO Socket] Joined room: ${roomName}`);
              setSocketConnectedForCounts(true);
            } catch (err) {
              ConsoleLog.double(
                `🔴 [BO Socket] Failed to join room ${roomName}`,
                err,
                "error"
              );
              // Flag false kalıyor → polling fallback devreye girer
            }

          },
          _OnNamespaceDisconnect() {
            SetStatus(4);
            setSocketConnectedForCounts(false);
            ConsoleLog.single("📡 [BO Socket] Disconnected");
          },
          _OnAnyEvent() {},
          export_progress(_nsConn: any, msg: any) {
            try {
              const data = JSON.parse(msg.Body);
              ConsoleLog.single("📡 [BO Socket] Export progress: " + data.progress + "%");
              emit("export_progress", data);
            } catch (e) {
              ConsoleLog.double("🔴 [BO Socket] Parse error (export_progress)", e, "error");
            }
          },
          export_complete(_nsConn: any, msg: any) {
            try {
              const data = JSON.parse(msg.Body);
              ConsoleLog.single("📡 [BO Socket] Export complete: " + data.job_id);
              emit("export_complete", data);

              if (data.status === "completed") {
                toast.success(
                  i18next.t("notifications.success"),
                  { description: i18next.t("export.exportReady") }
                );
              } else if (data.status === "failed") {
                toast.error(
                  i18next.t("texts.error"),
                  { description: i18next.t("export.exportFailed") }
                );
              }
            } catch (e) {
              ConsoleLog.double("🔴 [BO Socket] Parse error (export_complete)", e, "error");
            }
          },
          /**
           * client_new_request_counts
           * --
           * Backoffice → ilgili client'ın private odasına (`{clientSlug}_private`)
           * join olduktan sonra finans modülündeki yeni talep sayaçları
           * (yatırım / çekim / bonus) bu event üzerinden push edilir.
           * Server, sayaç değişiminde de aynı event ile günceller.
           */
          client_new_request_counts(_nsConn: any, msg: any) {
            try {
              const data = JSON.parse(msg.Body);
              ConsoleLog.single("📡 [BO Socket] New request counts received");
              emit("client_new_request_counts", data);
            } catch (e) {
              ConsoleLog.double("🔴 [BO Socket] Parse error (client_new_request_counts)", e, "error");
            }
          }
        }
      }, {
        reconnect : 2000,
        headers   : {
          "X-Auth-Token": token
        }
      });

      conn.connect(namespace);
      connectionRef.current = conn;

    } catch (err) {
      SetStatus(5);
      setSocketConnectedForCounts(false);
      ConsoleLog.double("🔴 [BO Socket] Connection failed", err, "error");
    } finally {
      isListeningRef.current = false;
    }

  }, [emit]);

  useEffect(() => {
    if (isLoggedIn && status === 0) {
      connect();
    } else if (!isLoggedIn && status > 0) {
      disconnect();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    return () => { disconnect(); };
  }, []);

  const contextValue: BackofficeSocketContextProps = {
    status,
    subscribe,
    unsubscribe
  };

  return (
    <BackofficeSocketContext.Provider value={contextValue}>
      {/**
        * Tek noktadan socket → request counts publish bridge'i.
        * Provider'ın ALTINA mount edilmesi şart (useSocketEvent context'e
        * ihtiyaç duyuyor) ve uygulamada **tek sefer** mount ediliyor.
        */}
      <RequestCountsSocketBridge />
      {children}
    </BackofficeSocketContext.Provider>
  );
};

export default BackofficeSocketContext;
